import React, { useEffect, useState } from "react";
import Listing from "../Listing";
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  getAllListings,
  getCurrentUserDetails,
  getListingByUserID,
  getAllUsersDetails
} from "../utils";
import { useAuth } from "../../contexts/AuthContext";

export default function MyListings() {
  const auth = useAuth();
  const [currentUser, setCurrentUser] = useState(null);
  const [listings, setListings] = useState([]);
  const [usersInfo, setUsersInfo] = useState({});

  // Sample columns for table
  const columns = [
    { Header: "Boat Listing", accessor: "boatListing" }, // name, year, id, thumb
    { Header: "Status", accessor: "status" },
    { Header: "Owner", accessor: "owner" },
    { Header: "Actions", accessor: "actions" },
  ];

  useEffect(() => {
    const fetchUserAndListings = async () => {
      try {
        const user = await getCurrentUserDetails();
        setCurrentUser(user);

        if (user?.role?.toLowerCase() === "admin") {
          const allUsers = await getAllUsersDetails();
          const userMap = {};
          if (allUsers) {
            allUsers.forEach((u) => {
              userMap[u.userId] = u;
            });
          }
          setUsersInfo(userMap);

          const allListings = await getAllListings();
          setListings(allListings || []);
        } else {
          // If Owner, no need for full users map, just owner details
          setUsersInfo({ [auth.currentUser.uid]: user });
          const ownerListings = await getListingByUserID(auth.currentUser.uid);
          setListings(ownerListings || []);
        }
      } catch (error) {
        console.error("Error fetching listings:", error);
      }
    };

    fetchUserAndListings();
    return () => {
      setListings([]);
      setUsersInfo({});
    };
  }, []);

  const getData = async () => {
    if (currentUser?.role?.toLowerCase() === "admin") {
      const allListings = await getAllListings();
      setListings(allListings || []);
    } else {
      const ownerListings = await getListingByUserID(auth.currentUser.uid);
      setListings(ownerListings || []);
    }
  };

  const parseLocation = (addressText) => {
    if (!addressText) return { city: "", state: "" };
    const parts = addressText.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      // Assuming format: City, State, Country
      return { city: parts[0], state: parts[1] };
    }
    return { city: "", state: "" };
  };

  const transformedData = listings?.map((listing) => {
    const owner = usersInfo[listing?.ownerId] || {};
    const location = parseLocation(listing?.storageAddressText);

    let avatarUrl = owner.avatar || null;
    if (avatarUrl && !avatarUrl.startsWith('http')) {
      avatarUrl = `${process.env.REACT_APP_BASE_URL}/images/${avatarUrl}`;
    }

    return {
      id: String(listing.id),
      name: listing?.listingTitle,
      year: listing?.year || "N/A",
      model: listing?.model || "Model",
      status: listing?.boatStatus || "Pending",
      backgroundImage: listing?.featured_image,
      boatListed: listing?.boatListed,
      boatType: listing?.boatType,
      city: location.city,
      state: location.state,
      ownerId: listing?.ownerId,
      ownerName: `${owner.firstName || ""} ${owner.lastName || ""}`.trim() || owner.email || "Unknown",
      ownerAvatar: avatarUrl,
      ownerInitials: owner.firstName && owner.lastName ? `${owner.firstName[0]}${owner.lastName[0]}`.toUpperCase() : "U",
    };
  }) || [];

  return (
    <>
      <div className="row">
        <div className="col">
          <div className="page-description">
            <Row>
              <Col xs={6}>
                <h1>
                  {currentUser?.role?.toLowerCase() === "admin"
                    ? "All Listings"
                    : "My Boats"}
                </h1>
              </Col>
              {currentUser?.role?.toLowerCase() !== "admin" && (
                <Col
                  xs={6}
                  className="d-flex justify-content-end align-items-center"
                >
                  <Link className="btn btn-primary" to="/dashboard/add-listing">
                    Add Boat
                  </Link>
                </Col>
              )}
            </Row>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="card calendar-container border-0 shadow-sm" style={{ borderRadius: "12px" }}>
            <div className="card-body p-0">
              {listings && listings.length > 0 ? (
                <Listing
                  data={transformedData}
                  columns={columns}
                  getData={getData}
                  isAdmin={currentUser?.role?.toLowerCase() === "admin"}
                />
              ) : (
                <p className="p-4 text-center text-muted">No listings available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
